"use client";
import { useStream } from "../../../context/StreamProvider";
import { FiMic, FiMicOff } from "react-icons/fi";
import { useStreamAvatar } from "../../../hooks/useStreamAvatar";
import { useAppSelector } from "../../../utils/hooks";
import type { UserStream } from "../../../types/StreamTypes";

function StreamThumb({
  stream,
  onSelect,
}: {
  stream: UserStream;
  onSelect: (peerId: string) => void;
}) {
  const { peerId, stream: mediaStream, isAudioEnabled, isLocal, isVideoEnabled } =
    stream;
  const { url, name, initials } = useStreamAvatar(stream);
  const isRemoteMuted = useAppSelector(
    (s) => s.PeerJsSlice.isRemoteMuted,
  );
  const displayName = isLocal ? "You" : name || "Participant";

  return (
    <button
      type="button"
      onClick={() => onSelect(peerId)}
      className="group relative aspect-video h-full w-40 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-zinc-900 shadow-lg ring-0 transition-all duration-200 hover:ring-2 hover:ring-themeColor focus:outline-none focus:ring-2 focus:ring-themeColor sm:w-56 lg:w-64 2xl:w-80"
      id={peerId}
    >
      {isVideoEnabled ? (
        <video
          style={isLocal ? { transform: "scaleX(-1)" } : undefined}
          className="h-full w-full object-cover"
          ref={(video) => video && (video.srcObject = mediaStream)}
          muted={isLocal || isRemoteMuted}
          autoPlay
          playsInline
        />
      ) : (
        <div className="relative h-full w-full">
          {url && (
            <div
              className="absolute inset-0 scale-110 bg-cover bg-center blur-xl"
              style={{ backgroundImage: `url(${url})` }}
              aria-hidden="true"
            />
          )}
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative grid h-full w-full place-content-center">
            {url ? (
              <img
                src={url}
                className="h-16 w-16 rounded-full object-cover ring-2 ring-white/30 sm:h-20 sm:w-20"
                alt=""
              />
            ) : (
              <div className="grid h-16 w-16 place-content-center rounded-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-xl font-semibold text-white ring-2 ring-white/30 sm:h-20 sm:w-20 sm:text-2xl">
                {initials}
              </div>
            )}
          </div>
          {!isLocal && (
            <audio
              autoPlay
              className="hidden"
              playsInline
              muted={isLocal || isRemoteMuted}
              ref={(audio) => {
                if (audio && mediaStream) audio.srcObject = mediaStream;
              }}
            />
          )}
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pb-2 pt-8">
        <div className="px-3 text-left text-xs font-medium text-white sm:text-sm">
          <span className="line-clamp-1">{displayName}</span>
        </div>
      </div>

      <div className="absolute right-2 top-2 grid h-7 w-7 place-content-center rounded-full bg-black/50 text-white backdrop-blur-md sm:h-8 sm:w-8">
        {isAudioEnabled ? (
          <FiMic className="text-sm sm:text-base" />
        ) : (
          <FiMicOff className="text-sm text-red-400 sm:text-base" />
        )}
      </div>
    </button>
  );
}

export default function VideoGrid() {
  const { userStreams, updateCurrentStream } = useStream();

  return (
    <div className="flex h-full items-center gap-3 overflow-x-auto overflow-y-hidden pb-1">
      {Object.values(userStreams)
        .filter((userStream) => userStream.currentVideo == false)
        .map((s) => (
          <StreamThumb
            key={s.peerId}
            stream={s}
            onSelect={updateCurrentStream}
          />
        ))}
    </div>
  );
}
